// ============ PRÉ-REMPLISSAGE PAR BLUEPRINT ============
// À la création d'un thème, chaque page reçoit un contenu par défaut.
// Pour le blueprint choisi, la page d'accueil et la navigation sont
// composées à partir de la structure du blueprint, afin que le thème
// soit utilisable immédiatement, même sans passer par l'IA.

import { getDefaultPageContent } from './defaultPageContent';
import { getBlueprint, type ThemeBlueprint } from '@/lib/themeBlueprints';

interface NavLink {
  label: string;
  href: string;
}

const LABEL_TO_HREF: Record<string, string> = {
  Accueil: '/',
  'Nos produits': '/produits',
  Produits: '/produits',
  Boutique: '/produits',
  'Nos services': '/service',
  Services: '/service',
  Chambres: '/produits',
  'Nos vendeurs': '/vendeurs',
  Vendeurs: '/vendeurs',
  Catégories: '/categories',
  Galerie: '/gallery',
  Expériences: '/project',
  'À propos': '/apropos',
  Blog: '/blog',
  Contact: '/contact',
  Réserver: '/booking',
  Marketplace: '/marketplace',
};

function navLinks(bp: ThemeBlueprint): NavLink[] {
  return bp.nav.map((label) => ({
    label,
    href: LABEL_TO_HREF[label] || '/',
  }));
}

/** Section de page d'accueil associée à une balise de bloc. */
const TAG_SECTION: Record<string, { cls: string; body: string }> = {
  products: { cls: 'section', body: '{{products limit="8"}}' },
  featured_products: { cls: 'section section-alt', body: '{{featured_products limit="8"}}' },
  services: { cls: 'section section-alt', body: '{{services}}' },
  portfolio: { cls: 'section', body: '{{portfolio}}' },
  team: { cls: 'section section-alt', body: '{{team}}' },
  partners: { cls: 'section', body: '{{partners}}' },
  gallery: { cls: 'section section-alt', body: '{{gallery limit="8"}}' },
  testimonials: { cls: 'section', body: '{{testimonials}}' },
  stats: { cls: 'section section-alt', body: '{{stats}}' },
  blog_posts: { cls: 'section', body: '{{blog_posts limit="3"}}' },
  faq: { cls: 'section section-alt', body: '{{faq}}' },
  booking_form: { cls: 'section', body: '{{booking_form}}' },
  contact_form: { cls: 'section section-alt', body: '{{contact_form}}' },
  marketplace_search: { cls: 'section', body: '{{marketplace_search placeholder="Que recherchez-vous ?"}}' },
  marketplace_categories: { cls: 'section', body: '{{marketplace_categories}}' },
  marketplace_products: { cls: 'section section-alt', body: '{{marketplace_products limit="8"}}' },
  marketplace_sellers: { cls: 'section', body: '{{marketplace_sellers limit="6"}}' },
};

function homeForBlueprint(bp: ThemeBlueprint): string {
  const sections: string[] = [];

  // Bannières d'accueil (slider) — toujours en tête de la page d'accueil.
  sections.push('<section class="hero-banners">{{hero_banners}}</section>');

  sections.push(
    `<section class="hero"><div class="hero-content"><h1>{{site_name}}</h1><p class="hero-subtitle">{{site_description}}</p><div class="hero-actions"><a href="/produits" class="btn btn-primary">Découvrir</a><a href="/contact" class="btn btn-outline">Nous contacter</a></div></div></section>`,
  );

  const seen = new Set<string>();
  bp.tags.forEach((tag) => {
    const key = tag.split(' ')[0];
    if (seen.has(key)) return;
    const section = TAG_SECTION[key];
    if (!section) return;
    seen.add(key);
    sections.push(`<section class="${section.cls}"><div class="container">${section.body}</div></section>`);
  });

  if (sections.length === 1) {
    sections.push('<section class="section"><div class="container">{{products limit="8"}}</div></section>');
  }

  return sections.join('\n');
}

function headerForBlueprint(bp: ThemeBlueprint): string {
  const links = navLinks(bp);
  const items = links
    .map((l, i) => `<li><a href="${l.href}" class="nav-link${i === 0 ? ' active' : ''}">${l.label}</a></li>`)
    .join('');

  return `<header class="site-header"><div class="container"><a href="/" class="logo">{{site_logo}}</a><nav class="main-nav" aria-label="Navigation principale"><ul class="nav-list">${items}</ul></nav><button class="hamburger" aria-label="Ouvrir le menu"><span class="hamburger-line"></span><span class="hamburger-line"></span><span class="hamburger-line"></span></button></div></header>`;
}

function navmenuForBlueprint(bp: ThemeBlueprint): string {
  const links = navLinks(bp);
  const items = links.map((l) => `<li><a href="${l.href}" class="navmenu-link">${l.label}</a></li>`).join('');
  return `<nav class="navmenu" aria-label="Menu principal"><ul class="navmenu-list">${items}</ul></nav>`;
}

/**
 * Contenu par défaut d'une page pour un blueprint donné.
 * La page d'accueil et la navigation suivent la structure du blueprint ;
 * les autres pages utilisent les gabarits par défaut.
 */
export function getBlueprintPageContent(pageKey: string, blueprintId?: string | null): string {
  const bp = getBlueprint(blueprintId);
  if (!bp) return getDefaultPageContent(pageKey);

  if (pageKey === 'home') return homeForBlueprint(bp);
  if (pageKey === 'header') return headerForBlueprint(bp);
  if (pageKey === 'navmenu') return navmenuForBlueprint(bp);

  return getDefaultPageContent(pageKey);
}

/** Titre de navigation par défaut pour le blueprint (enregistré dans sitewebthemecontenu). */
export function blueprintNavTitle(blueprintId?: string | null): string {
  const bp = getBlueprint(blueprintId);
  if (!bp) return 'Accueil | À propos | Contact';
  const labels = bp.nav;
  if (!labels.includes('À propos')) labels.push('À propos');
  if (!labels.includes('Contact')) labels.push('Contact');
  return labels.join(' | ');
}